// ─── Kith Menu — Refactored ──────────────────────────────────────────────────
// External deps (expected on window before this runs):
//   window._kithMenuData, window._kithProductData, window._kithMarketingLines
//   window._kithRewardsCustomer (optional alias: window.rewardsCustomer)
// External helpers expected elsewhere in the codebase:
//   isMobile(), lockMenuScroll(), applyDrawerStyles(), applyHeaderOffset(),
//   openDrawer(), closeDrawer(), closeAllDrawers(), setHamburgerOpen(), debounce()

(document.readyState === 'loading'
  ? document.addEventListener('DOMContentLoaded', initKithMenu)
  : initKithMenu());

function initKithMenu() {
  // ─── DOM refs ──────────────────────────────────────────────────────────────
  const topbarBtns = document.querySelectorAll('.kith-menu-nav__btn');
  const hamburger  = document.querySelector('.kith-menu-drawer__trigger');
  const drawer0    = document.getElementById('kith-menu-drawer-0');
  const drawer1    = document.getElementById('kith-menu-drawer-1');
  const drawer2    = document.getElementById('kith-menu-drawer-2');
  const searchModal = document.getElementById('search-modal');
  const cartDrawer = document.getElementById('topshelf-kith-cart-drawer');
  const subCol     = drawer1?.querySelector('[data-kith-menu-sub]');
  const subsubCol  = drawer2?.querySelector('[data-kith-menu-subsub]');
  let suppressDrawerClicksUntil = 0;

  // ─── Safe fallbacks ────────────────────────────────────────────────────────
  const safeIsMobile = typeof window.isMobile === 'function'
    ? window.isMobile
    : () => window.matchMedia('(max-width: 900px)').matches;

  const safeLockMenuScroll = typeof window.lockMenuScroll === 'function'
    ? window.lockMenuScroll
    : () => {
        if (safeIsMobile()) return;
        document.documentElement.classList.add('kith-menu-locked');
        document.body.classList.add('kith-menu-locked');
      };

  const safeApplyDrawerStyles = typeof window.applyDrawerStyles === 'function'
    ? window.applyDrawerStyles
    : () => {};

  const safeApplyHeaderOffset = typeof window.applyHeaderOffset === 'function'
    ? window.applyHeaderOffset
    : () => {};

  const safeSetHamburgerOpen = typeof window.setHamburgerOpen === 'function'
    ? window.setHamburgerOpen
    : (isOpen) => {
        if (!hamburger) return;
        hamburger.classList.toggle('is-open', !!isOpen);
        hamburger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      };

  const safeOpenDrawer = typeof window.openDrawer === 'function'
    ? window.openDrawer
    : (el) => {
        if (!el) return;
        el.removeAttribute('hidden');
        el.classList.add('is-open');
        el.style.display = 'block';
      };

  const safeCloseDrawer = typeof window.closeDrawer === 'function'
    ? window.closeDrawer
    : (el) => {
        if (!el) return;
        el.classList.remove('is-open');
        el.setAttribute('hidden', '');
        el.style.display = '';
      };

  const safeCloseAllDrawers = typeof window.closeAllDrawers === 'function'
    ? window.closeAllDrawers
    : (resetHamburger = false) => {
        [drawer0, drawer1, drawer2].forEach((el) => {
          if (!el) return;
          el.classList.remove('is-open');
          el.setAttribute('hidden', '');
          el.style.display = '';
        });
        document.body.classList.remove('kith-menu-open');
        document.documentElement.classList.remove('kith-menu-locked');
        document.body.classList.remove('kith-menu-locked');
        syncHamburgerState();
      };

  const safeDebounce = typeof window.debounce === 'function'
    ? window.debounce
    : (fn) => {
        let t;
        return (...args) => {
          clearTimeout(t);
          t = setTimeout(() => fn(...args), 100);
        };
      };

  /** Rewards / account URLs come from Liquid as window._kithRewardsCustomer */
  function getRewardsContext() {
    return window.rewardsCustomer || window._kithRewardsCustomer || {};
  }

  function initSearchPlaceholder() {
    const lines = window._kithMarketingLines?.filter(Boolean);
    const input =
      document.querySelector('.kith-menu-search input[type="search"]') ||
      document.querySelector('.kith-menu-search .search-input');
    if (input && lines?.length && !input.getAttribute('placeholder')) {
      input.setAttribute('placeholder', lines[0]);
    }
  }

  function isAnyDrawerOpen() {
    return [drawer0, drawer1, drawer2].some(d => d?.classList.contains('is-open'));
  }

  function syncHamburgerState() {
    safeSetHamburgerOpen(isAnyDrawerOpen());
  }

  function openKithSearch() {
    safeCloseAllDrawers(true);
    if (typeof searchModal?.showDialog === 'function') {
      searchModal.showDialog();
      setTimeout(() => {
        searchModal.querySelector('input[type="search"]')?.focus();
      }, 50);
      return true;
    }
    return false;
  }

  function openKithCart() {
    safeCloseAllDrawers(true);
    if (typeof cartDrawer?.open === 'function') {
      cartDrawer.open();
      return true;
    }
    return false;
  }

  // ─── State ─────────────────────────────────────────────────────────────────
  let currentMainIdx = null;
  let suppressUntil  = 0;

  // Rewards points: fetched once and cached
  let rewardsPointsValue   = null;
  let rewardsPointsPromise = null;

  function configureDrawerContent(drawer) {
    if (!drawer) return;
    const content = drawer.querySelector('.kith-menu-drawer__content');
    if (!(content instanceof HTMLElement)) return;

    drawer.style.overflow = 'hidden';
    content.style.display = 'flex';
    content.style.flexDirection = 'column';
    content.style.flex = '1 1 auto';
    content.style.minHeight = '0';
    content.style.height = '100%';
    content.style.maxHeight = '100%';
    content.style.overflowY = 'auto';
    content.style.overflowX = 'hidden';
    content.style.overscrollBehavior = 'contain';
    content.style.webkitOverflowScrolling = 'touch';
    content.style.pointerEvents = 'auto';

    if (safeIsMobile()) {
      drawer.style.touchAction = 'pan-y';
      content.style.touchAction = 'pan-y';

      let spacer = content.querySelector('.kith-menu-drawer__mobile-bottom-spacer');
      if (!(spacer instanceof HTMLElement)) {
        spacer = document.createElement('div');
        spacer.className = 'kith-menu-drawer__mobile-bottom-spacer';
        spacer.setAttribute('aria-hidden', 'true');
        spacer.style.cssText = 'display:block;flex:0 0 auto;width:100%;height:60px;pointer-events:none;';
        content.appendChild(spacer);
      } else {
        content.appendChild(spacer);
      }
    }
  }

  function markScrollGesture(drawer) {
    if (!safeIsMobile() || !drawer) return;

    const content = drawer.querySelector('.kith-menu-drawer__content');
    if (!(content instanceof HTMLElement) || content.dataset.scrollGuardBound === 'true') return;

    content.dataset.scrollGuardBound = 'true';
    content.addEventListener('scroll', () => {
      suppressDrawerClicksUntil = Date.now() + 150;
    }, { passive: true });
  }

  // ─── Init ──────────────────────────────────────────────────────────────────
  if (subCol) {
    subCol.querySelectorAll('.kith-menu-arrow-btn, .kith-menu-sub-link')
          .forEach(el => el.classList.remove('active'));
  }
  safeSetHamburgerOpen(false);
  mountDrawersIntoHeader();
  safeApplyHeaderOffset();
  [drawer0, drawer1, drawer2].forEach(configureDrawerContent);
  [drawer0, drawer1, drawer2].forEach(markScrollGesture);
  initSearchPlaceholder();
  bindEvents();

  // ══════════════════════════════════════════════════════════════════════════
  // HTML BUILDERS
  // ══════════════════════════════════════════════════════════════════════════

  function icon(src, mod = '') {
    return src
      ? `<span class="kith-menu-icon${mod}" aria-hidden="true" style="background-image:url('${src}');"></span>`
      : `<span class="kith-menu-icon kith-menu-icon--default${mod}"></span>`;
  }

  function chevronBox() {
    return `
      <span class="kith-menu-row-chevron" aria-hidden="true">
        <svg viewBox="0 0 16 16" fill="none" focusable="false" aria-hidden="true">
          <path d="M6 3.5L10.5 8L6 12.5" stroke="currentColor" stroke-width="2.25" stroke-linecap="round" stroke-linejoin="round"></path>
        </svg>
      </span>`;
  }

  function drawerRowContent(title, iconMarkup = '', showSubtitle = true) {
    return `
      <span class="kith-menu-row-leading">
        ${iconMarkup}
        <span class="kith-menu-row-copy">
          <span class="kith-menu-row-title">${title}</span>
          ${showSubtitle ? '<span class="kith-menu-row-subtitle">Browse category</span>' : ''}
        </span>
      </span>`;
  }

  function buildRewardsCardHTML() {
    const r = getRewardsContext();
    const loggedIn = r.isLoggedIn;
    return `
      <a href="${r.rewardsPageUrl || r.accountUrl || '/account/login'}" class="kith-menu-rewards-link">
        <div class="kith-menu-rewards-card" data-influence-rewards>
          <div class="kith-menu-rewards-card__media">
            <img src="${r.rewardsCoinUrl || ''}" alt="Reefer Rewards" class="kith-menu-rewards-card__coin">
          </div>
          <div class="kith-menu-rewards-card__content">
            <div class="kith-menu-rewards-card__eyebrow">${loggedIn ? 'Reefer Rewards' : 'Sign in to view points'}</div>
            <div class="kith-menu-rewards-card__points">
              ${loggedIn
                ? '<span class="influenceio-points">0</span> <span class="kith-menu-rewards-card__points-label">Points</span>'
                : '<span>Rewards</span>'}
            </div>
            <div class="kith-menu-rewards-card__cta">Redeem <span aria-hidden="true">→</span></div>
          </div>
        </div>
        ${loggedIn ? `
          <div class="influenceio-widgets-init"
            data-shop="${r.shop || ''}"
            data-platform="shopify"
            data-customer-email="${r.customerEmail || ''}"
            data-customer-id="${r.customerId || ''}"
            data-customer-digest="${r.customerDigest || ''}">
          </div>` : ''}
      </a>`;
  }

  function buildUtilityLinksHTML() {
    const r = getRewardsContext();
    const links = [
      { href: r.accountUrl || '/account/login', label: 'My Account',
        svg: '<path d="M19 21a7 7 0 0 0-14 0" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/><circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="1.8"/>' },
      { href: r.helpCenterUrl || '/pages/help-center', label: 'Help Center',
        svg: '<circle cx="12" cy="12" r="8" stroke="currentColor" stroke-width="1.8"/><circle cx="12" cy="12" r="2.75" stroke="currentColor" stroke-width="1.8"/><path d="M12 4v2.2M12 17.8V20M4 12h2.2M17.8 12H20" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' },
      { href: r.trackOrderUrl || '/account', label: 'Track Order',
        svg: '<path d="M3.5 7.5h10v7h-10z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><path d="M13.5 10h3.2l2.3 2.4v2.1h-5.5z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/><circle cx="8" cy="17" r="1.6" stroke="currentColor" stroke-width="1.8"/><circle cx="17.2" cy="17" r="1.6" stroke="currentColor" stroke-width="1.8"/><path d="M6.5 10.5h4" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>' },
    ];
    return `<div class="kith-menu-utility-links">${links.map(({ href, label, svg }) => `
      <a href="${href}" class="kith-menu-utility-link">
        <span class="kith-menu-utility-link__main">
          <span class="kith-menu-utility-link__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">${svg}</svg>
          </span>
          <span class="kith-menu-utility-link__label">${label}</span>
        </span>
        <span class="kith-menu-utility-link__chevron" aria-hidden="true">›</span>
      </a>`).join('')}
    </div>`;
  }

  function buildFooterHTML() {
    return `
      <div class="kith-menu-drawer__footer">
        <div class="kith-menu-footer__top">
          ${buildUtilityLinksHTML()}
          ${buildRewardsCardHTML()}
        </div>
        <div class="kith-menu-footer__motto">
          <div class="kith-menu-footer__motto-title">Top Shelf Aquatics</div>
          <div class="kith-menu-footer__motto-subtitle">Building Reefs Since 2012</div>
        </div>
      </div>`;
  }

  // ══════════════════════════════════════════════════════════════════════════
  // REWARDS HYDRATION
  // ══════════════════════════════════════════════════════════════════════════

  function applyRewardsPoints(root) {
    root.querySelectorAll('.influenceio-points')
        .forEach(el => { el.textContent = rewardsPointsValue || ''; });
  }

  function hydrateRewardsPoints(root) {
    const r = getRewardsContext();
    if (!r.isLoggedIn) return;
    if (rewardsPointsValue !== null) { applyRewardsPoints(root); return; }

    const d = root.querySelector('.influenceio-widgets-init')?.dataset;
    if (!d?.shop || !d.customerId || !d.customerEmail || !d.customerDigest) return;

    rewardsPointsPromise ??= fetch(
      'https://launcher.api.influence.io/launcher/v1/customer?' +
        new URLSearchParams({ customer_id: d.customerId, customer_email: d.customerEmail, shop: d.shop, digest: d.customerDigest }),
      { headers: { 'content-type': 'application/json' } }
    )
      .then(res => res.json())
      .then(customer => { rewardsPointsValue = Number(customer?.pointBalance || 0).toLocaleString(); return rewardsPointsValue; })
      .catch(() => { rewardsPointsValue = '0'; return rewardsPointsValue; });

    rewardsPointsPromise.then(() => applyRewardsPoints(root));
  }

  // ══════════════════════════════════════════════════════════════════════════
  // MENU RENDERERS
  // ══════════════════════════════════════════════════════════════════════════

  function staggerAnimate(parent, selector, delay = 50) {
    requestAnimationFrame(() => {
      parent.querySelectorAll(selector).forEach((el, i) =>
        setTimeout(() => el.classList.add('animate'), i * delay));
    });
  }

  function renderMainMenuMobile() {
    if (!drawer0) return;
    const data = window._kithMenuData || [];
    if (!data.length) return;

    let container = drawer0.querySelector('[data-kith-menu-main]');
    if (!container) {
      container = document.createElement('div');
      container.setAttribute('data-kith-menu-main', '');
      (drawer0.querySelector('.kith-menu-drawer__content') || drawer0).appendChild(container);
    }

    container.innerHTML = `
      <ul class="kith-menu-main-list">
        ${data.map((link, i) => `
          <li class="kith-menu-main-item">
            ${link.links?.length
              ? `<button class="kith-menu-main-link" data-kith-menu-main-index="${i}" type="button">${drawerRowContent(link.title, icon(link.icon), true)}${chevronBox()}</button>`
              : `<a href="${link.url}" class="kith-menu-main-link">${drawerRowContent(link.title, icon(link.icon), false)}</a>`}
          </li>`).join('')}
      </ul>
      ${buildFooterHTML()}`;

    hydrateRewardsPoints(container);
  }

  function renderSubMenu(mainIdx) {
    if (!subCol) return;
    const mainLink = window._kithMenuData?.[mainIdx];
    if (!mainLink?.links) { subCol.innerHTML = '<div class="kith-menu-empty">No sub-links</div>'; return; }

    const listHTML = `<ul>${mainLink.links.map((sub, i) => {
      const hasChildren = sub.links?.length > 0;
      return `<li class="kith-menu-sub-item">
        <a href="${sub.url}" class="kith-menu-sub-link kith-menu-sub-link--first">
          ${drawerRowContent(sub.title, icon(sub.icon, ' kith-menu-icon--first'), hasChildren)}
        </a>
        ${hasChildren
          ? `<button class="kith-menu-arrow-btn"
              data-kith-menu-sub-index="${i}" aria-label="Open submenu">
              ${chevronBox()}
            </button>`
          : ''}
      </li>`;
    }).join('')}</ul>`;

    subCol.innerHTML = safeIsMobile() ? listHTML : listHTML + buildFooterHTML();
    if (!safeIsMobile()) hydrateRewardsPoints(subCol);

    staggerAnimate(subCol, '.kith-menu-sub-link');
    topbarBtns.forEach((btn, i) => btn.classList.toggle('kith-menu-nav__btn--active', i === mainIdx));
  }

  function renderSubSubMenu(mainIdx, subIdx) {
    if (!subsubCol) return;
    const subLink = window._kithMenuData?.[mainIdx]?.links?.[subIdx];
    if (!subLink?.links) { subsubCol.innerHTML = '<div class="kith-menu-empty">No links</div>'; return; }

    subsubCol.innerHTML = `
      <div class="kith-menu-subsub-header">
        <h3 class="kith-menu-subsub-header-title">${subLink.title || 'Collection'}</h3>
      </div>
      <div class="kith-menu-subsub-grid">
        ${subLink.links.map(sub => {
          const badge = sub.badge
            ? `<div class="kith-menu-badge" style="background:${sub.badge_color || '#ff4444'}">${sub.badge}</div>` : '';
          return `<a href="${sub.url}" class="kith-menu-subsub-item">${icon(sub.icon, ' kith-menu-icon--grid')}<div class="kith-menu-subsub-title">${sub.title}</div>${badge}</a>`;
        }).join('')}
      </div>`;

    staggerAnimate(subsubCol, '.kith-menu-subsub-item');
    loadProductsForMenu(subLink);

    setTimeout(() => {
      drawer2?.querySelector('.kith-menu-drawer__content')?.scrollTo?.({ top: 0 });
    }, 50);
  }

  // ══════════════════════════════════════════════════════════════════════════
  // PRODUCT CAROUSEL
  // ══════════════════════════════════════════════════════════════════════════

  function formatPrice(price) {
    if (price == null || price === '') return '$0<sup>.00</sup>';
    const str = String(price).trim();
    let val = str.includes('.') ? parseFloat(str) : (() => {
      const n = parseInt(str, 10);
      return isNaN(n) ? NaN : n >= 1000 ? n / 100 : n;
    })();
    if (!isFinite(val)) return '$0<sup>.00</sup>';
    const cents = Math.round((val % 1) * 100).toString().padStart(2, '0');
    return `$${Math.floor(val).toLocaleString('en-US')}<sup>.${cents}</sup>`;
  }

  function getProductData() {
    if (window._kithProductData) return window._kithProductData;

    const dataScript = document.getElementById('kith-product-data-json');
    if (!dataScript) {
      window._kithProductData = {};
      return window._kithProductData;
    }

    try {
      window._kithProductData = JSON.parse(dataScript.textContent || '{}');
    } catch (_) {
      window._kithProductData = {};
    }

    return window._kithProductData;
  }

  function loadProductsForMenu(menuItem) {
    const container = document.getElementById('kith-menu-product-carousel');
    if (!container) return;
    const productData = getProductData();
    const handle = menuItem.url?.match(/\/collections\/([^/?]+)/)?.[1];
    const data   = handle && productData?.[handle]
      ? productData[handle]
      : Object.values(productData || {})[0];

    if (data?.products?.length) {
      renderProductCarousel(data.products, container, data.title || 'Products');
    } else {
      container.style.display = 'none';
    }
  }

  function renderProductCarousel(products, container, collectionName = 'Products') {
    if (!products?.length) { container.style.display = 'none'; return; }

    container.innerHTML = `
      <div class="kith-product-carousel" data-carousel-id="kith-menu-products">
        <div class="kith-product-carousel__header">
          <h4 class="kith-product-carousel__title">FEATURED ${collectionName.toUpperCase()}</h4>
        </div>
        <div class="kith-product-carousel__container">
          <div class="kith-product-carousel__track">
            ${products.map(p => `
              <div class="kith-product-carousel__item" data-product-id="${p.id}">
                <a href="${p.url}" class="kith-product-carousel__link">
                  <div class="kith-product-carousel__image">
                    <img src="${p.featured_image || p.images?.[0]}" alt="${p.title}" loading="lazy" width="300" height="300">
                  </div>
                  <div class="kith-product-carousel__content">
                    <h4 class="kith-product-carousel__title">${p.title}</h4>
                    <div class="kith-product-carousel__price">
                      ${p.compare_at_price && p.compare_at_price !== p.price
                        ? `<span class="kith-product-carousel__price--sale">${formatPrice(p.price)}</span><span class="kith-product-carousel__price--compare">${formatPrice(p.compare_at_price)}</span>`
                        : `<span class="kith-product-carousel__price--regular">${formatPrice(p.price)}</span>`}
                    </div>
                  </div>
                </a>
              </div>`).join('')}
          </div>
        </div>
        <div class="kith-product-carousel__controls">
          <button class="kith-product-carousel__control-btn" data-direction="prev">‹</button>
          <button class="kith-product-carousel__control-btn" data-direction="next">›</button>
        </div>
      </div>`;

    container.style.display = 'block';
    container.style.paddingBottom = safeIsMobile() ? '260px' : '';
    staggerAnimate(container, '.kith-product-carousel__item');
    setTimeout(() => initCarousel(container), 600);
  }

  function initCarousel(container) {
    const track   = container.querySelector('.kith-product-carousel__track');
    const items   = container.querySelectorAll('.kith-product-carousel__item');
    const prevBtn = container.querySelector('[data-direction="prev"]');
    const nextBtn = container.querySelector('[data-direction="next"]');
    if (items.length <= 3) return;

    const frag = document.createDocumentFragment();
    for (let i = 0; i < 3; i++) items.forEach(item => frag.appendChild(item.cloneNode(true)));
    track.appendChild(frag);

    const play  = () => {
      if (safeIsMobile()) return;
      track.style.animation = 'carousel-scroll 15s linear infinite';
    };
    const pause = () => { track.style.animationPlayState = 'paused'; };
    let manualTimeout;
    let touchStartX = 0;
    let touchStartY = 0;
    let touchBaseX = 0;
    let touchDragging = false;
    let touchHorizontal = false;

    const getTrackX = () => new DOMMatrix(getComputedStyle(track).transform).m41;
    const getItemWidth = () => items[0].offsetWidth + 8;
    const stopAnimation = () => {
      const x = getTrackX();
      track.style.animation = 'none';
      track.style.transform = `translateX(${x}px)`;
      return x;
    };

    setTimeout(play, 2000);
    track.addEventListener('animationend', () => { track.style.animation = 'none'; track.offsetHeight; play(); });
    container.addEventListener('mouseenter', pause);
    container.addEventListener('mouseleave', () => {
      clearTimeout(manualTimeout);
      play();
    });

    if (prevBtn && nextBtn) {
      const step = (dir) => {
        const x = stopAnimation();
        const w = getItemWidth();
        track.style.transform = `translateX(${dir === 'next' ? x - w : x + w}px)`;
        clearTimeout(manualTimeout);
        manualTimeout = setTimeout(play, 1500);
      };
      prevBtn.addEventListener('click', () => step('prev'));
      nextBtn.addEventListener('click', () => step('next'));
    }

    let wheelTimer;
    container.addEventListener('wheel', (e) => {
      e.preventDefault();
      if (wheelTimer) return;
      const x = stopAnimation();
      track.style.transform = `translateX(${x - e.deltaX * 0.5}px)`;
      wheelTimer = setTimeout(() => { wheelTimer = null; }, 16);
      clearTimeout(manualTimeout);
      manualTimeout = setTimeout(play, 2000);
    }, { passive: false });

    container.addEventListener('touchstart', (e) => {
      if (!safeIsMobile() || e.touches.length !== 1) return;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchBaseX = stopAnimation();
      touchDragging = true;
      touchHorizontal = false;
      clearTimeout(manualTimeout);
    }, { passive: true });

    container.addEventListener('touchmove', (e) => {
      if (!safeIsMobile() || !touchDragging || e.touches.length !== 1) return;
      const deltaX = e.touches[0].clientX - touchStartX;
      const deltaY = e.touches[0].clientY - touchStartY;

      if (!touchHorizontal) {
        if (Math.abs(deltaY) > Math.abs(deltaX)) {
          touchDragging = false;
          return;
        }
        if (Math.abs(deltaX) < 8) return;
        touchHorizontal = true;
      }

      track.style.transform = `translateX(${touchBaseX + deltaX}px)`;
      e.preventDefault();
    }, { passive: false });

    container.addEventListener('touchend', (e) => {
      if (!safeIsMobile()) return;
      const deltaX = touchDragging && e.changedTouches.length ? e.changedTouches[0].clientX - touchStartX : 0;
      const stepWidth = getItemWidth();
      const threshold = Math.min(60, stepWidth * 0.22);

      if (touchHorizontal && Math.abs(deltaX) > threshold) {
        track.style.transform = `translateX(${touchBaseX + (deltaX < 0 ? -stepWidth : stepWidth)}px)`;
      } else {
        track.style.transform = `translateX(${touchBaseX}px)`;
      }

      touchDragging = false;
      touchHorizontal = false;
    }, { passive: true });
  }

  // ══════════════════════════════════════════════════════════════════════════
  // RIPPLE
  // ══════════════════════════════════════════════════════════════════════════

  function createRipple(e, target) {
    target.querySelector('.ripple')?.remove();
    const ripple = document.createElement('span');
    const rect   = target.getBoundingClientRect();
    const size   = Math.max(rect.width, rect.height) * 0.8;
    ripple.className = 'ripple';
    ripple.style.cssText = `width:${size}px;height:${size}px;left:${e.clientX - rect.left - size / 2}px;top:${e.clientY - rect.top - size / 2}px;background:rgba(0,0,0,.3);border-radius:50%;position:absolute;pointer-events:none;z-index:9999;transform:scale(0);transform-origin:center;animation:ripple-animation .6s linear;`;
    target.appendChild(ripple);
    setTimeout(() => ripple.remove(), 600);
  }


  // ══════════════════════════════════════════════════════════════════════════
  // EVENT BINDING
  // ══════════════════════════════════════════════════════════════════════════

  function openSubMenu(idx) {
    renderSubMenu(idx);
    safeCloseDrawer(drawer2);
    safeOpenDrawer(drawer1);
    currentMainIdx = idx;
    syncHamburgerState();
  }

  function handleTopbarClick(i) {
    if (safeIsMobile()) return;
    const link = window._kithMenuData?.[i];
    if (currentMainIdx === i && drawer1?.classList.contains('is-open')) {
      safeCloseAllDrawers();
      if (link?.url) window.location.assign(String(link.url));
      currentMainIdx = null;
    } else if (link?.links?.length) {
      openSubMenu(i);
    } else if (link?.url) {
      window.location.assign(String(link.url));
      currentMainIdx = null;
    }
  }

  function bindEvents() {
    topbarBtns.forEach((btn, i) => btn.addEventListener('click', safeDebounce(() => handleTopbarClick(i), 100)));

    document.querySelectorAll('.kith-menu-nav__btn--dropdown').forEach(btn => {
      btn.addEventListener('click', safeDebounce(() => {
        handleTopbarClick(parseInt(btn.getAttribute('data-kith-menu-main-index') || '0', 10));
      }, 100));
    });

    if (hamburger) {
      hamburger.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();

        if (safeIsMobile()) {
          if (isAnyDrawerOpen()) {
            safeCloseAllDrawers(true);
            currentMainIdx = null;
            if (subCol) subCol.innerHTML = '';
            if (subsubCol) subsubCol.innerHTML = '';
            return;
          }

          [drawer1, drawer2].forEach(d => { d?.classList.remove('is-open'); d?.setAttribute('hidden', ''); });
          if (subCol) subCol.innerHTML = '';
          if (subsubCol) subsubCol.innerHTML = '';

          drawer0?.classList.remove('is-open');
          drawer0?.setAttribute('hidden', '');
          currentMainIdx = null;
          suppressUntil = Date.now() + 400;
          renderMainMenuMobile();

          if (drawer0) {
            drawer0.style.pointerEvents = 'none';
            setTimeout(() => { drawer0.style.pointerEvents = 'auto'; }, 250);
          }

          drawer0?.removeAttribute('hidden');
          drawer0?.classList.add('is-open');
          configureDrawerContent(drawer0);
          safeLockMenuScroll();
          syncHamburgerState();
          safeApplyDrawerStyles(drawer0);
          return;
        }

        if (isAnyDrawerOpen()) {
          safeCloseAllDrawers(true);
          currentMainIdx = null;
          if (subCol) subCol.innerHTML = '';
          if (subsubCol) subsubCol.innerHTML = '';
          return;
        }
        openSubMenu(0);
      });
    }

    document.querySelectorAll('.kith-menu-drawer__overlay')
      .forEach(o => o.addEventListener('click', () => { if (!safeIsMobile()) safeCloseAllDrawers(true); }));

    document.querySelectorAll('.kith-menu-back-btn').forEach(btn => {
      btn.addEventListener('click', () => {
        const to = btn.getAttribute('data-kith-menu-back-target');
        if (to === 'drawer0') { safeCloseDrawer(drawer1, true); renderMainMenuMobile(); safeOpenDrawer(drawer0); configureDrawerContent(drawer0); syncHamburgerState(); }
        else if (to === 'drawer1') { safeCloseDrawer(drawer2, true); safeOpenDrawer(drawer1); configureDrawerContent(drawer1); syncHamburgerState(); }
      });
    });

    document.querySelectorAll('.kith-menu-drawer__close, .kith-menu-close-btn')
      .forEach(btn => btn.addEventListener('click', () => safeCloseAllDrawers(true)));

    document.querySelectorAll('[data-kith-cart-open]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        openKithCart();
      });
    });

    document.querySelectorAll('[data-kith-search-open]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        openKithSearch();
      });
    });

    drawer0?.addEventListener('click', (e) => {
      if (Date.now() < suppressDrawerClicksUntil) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      if (Date.now() < suppressUntil) { e.preventDefault(); e.stopImmediatePropagation(); return; }
      const mainLink = e.target.closest('.kith-menu-main-link');
      if (!mainLink) return;

      createRipple(e, mainLink.closest('li') || mainLink);

      const btn = mainLink.matches('.kith-menu-main-link[data-kith-menu-main-index]') ? mainLink : null;
      if (!btn) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      currentMainIdx = parseInt(btn.getAttribute('data-kith-menu-main-index'), 10);
      setTimeout(() => {
        renderSubMenu(currentMainIdx);
        safeCloseDrawer(drawer0, true);
        safeOpenDrawer(drawer1);
        configureDrawerContent(drawer1);
        syncHamburgerState();
      }, 120);
    });

    subCol?.addEventListener('click', (e) => {
      if (Date.now() < suppressDrawerClicksUntil) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      const arrow = e.target.closest('.kith-menu-arrow-btn');
      if (arrow) {
        e.preventDefault();
        e.stopPropagation();
        const i = parseInt(arrow.getAttribute('data-kith-menu-sub-index'), 10);
        subCol.querySelectorAll('.kith-menu-arrow-btn, .kith-menu-sub-link')
              .forEach(el => el.classList.remove('active'));
        arrow.classList.add('active');
        arrow.closest('.kith-menu-sub-item')?.querySelector('.kith-menu-sub-link')?.classList.add('active');
        renderSubSubMenu(currentMainIdx, i);
        safeOpenDrawer(drawer2);
        configureDrawerContent(drawer2);
        syncHamburgerState();
        setTimeout(() => drawer2?.querySelector('.kith-menu-drawer__content')?.scrollTo?.({ top: 0 }), 100);
        return;
      }
      const link = e.target.closest('.kith-menu-sub-link');
      if (link) createRipple(e, link.closest('li') || link);
    });

    document.addEventListener('click', (e) => {
      if (![drawer0, drawer1, drawer2].some(d => d?.classList.contains('is-open'))) return;
      if (e.target.closest('.kith-menu-drawer__content, .kith-menu-nav__btn, .kith-menu-nav__btn--dropdown, .kith-menu-drawer__trigger, .kith-header-component')) return;
      safeCloseAllDrawers(true);
    });

    document.addEventListener('click', (e) => {
      if (Date.now() < suppressDrawerClicksUntil && e.target.closest('.kith-menu-drawer__content')) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      const card = e.target.closest('.bento-card');
      if (!card || e.target.closest('button, input, select, label, a:not(.bento-card__link)')) return;
      createRipple(e, card);
    });

  }

  // ══════════════════════════════════════════════════════════════════════════
  // UTILS
  // ══════════════════════════════════════════════════════════════════════════

  function mountDrawersIntoHeader() {
    const target = document.querySelector('.kith-header-component') ||
                   document.querySelector('#header-group');

    if (!target) return;

    [drawer0, drawer1, drawer2].forEach(d => {
      if (d && d.parentElement !== target) {
        try {
          target.appendChild(d);
        } catch (_) {}
      }
    });
  }
}
